import { useCallback, useEffect, useState } from "react";
import * as platformService from "../services/platformService";
import { EMPTY_MODULE_ACCESS } from "../constants/platformModules";

// One Finance / DMS access answer per signed-in identity, shared by the sidebar and
// every Finance / Dealership screen (GET platform/access). The same cache pattern as
// useHrAccess: keyed by the token (a Super Admin "viewing as" a vendor swaps it
// without a reload), refetched when a screen announces a change, and a failed
// request is retried rather than remembered.
let cached = null; // { key, value }
let inflight = null; // { key, promise }

export const MODULE_ACCESS_CHANGED = "module-access-changed";
export const notifyModuleAccessChanged = () => {
  cached = null;
  window.dispatchEvent(new Event(MODULE_ACCESS_CHANGED));
};

const identityKey = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

const fetchOnce = () => {
  const key = identityKey();
  if (cached && cached.key === key) return Promise.resolve(cached.value);
  if (inflight && inflight.key === key) return inflight.promise;
  const promise = platformService
    .getAccess()
    .then((a) => {
      const value = a || EMPTY_MODULE_ACCESS;
      if (value !== EMPTY_MODULE_ACCESS) cached = { key, value };
      return value;
    })
    .finally(() => {
      if (inflight && inflight.promise === promise) inflight = null;
    });
  inflight = { key, promise };
  return promise;
};

// Test hook: forget what was fetched.
export const resetModuleAccessCache = () => {
  cached = null;
  inflight = null;
};

// `enabled` false (a Super Admin, who has no tenant) never calls the API.
export default function useModuleAccess(enabled = true) {
  const key = identityKey();
  const [access, setAccess] = useState(cached && cached.key === key ? cached.value : EMPTY_MODULE_ACCESS);
  const [loaded, setLoaded] = useState((!!cached && cached.key === key) || !enabled);

  const load = useCallback(async () => {
    const a = await fetchOnce();
    setAccess(a);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    setLoaded(!!cached && cached.key === key);
    load();
    window.addEventListener(MODULE_ACCESS_CHANGED, load);
    return () => window.removeEventListener(MODULE_ACCESS_CHANGED, load);
  }, [enabled, key, load]);

  return { access: enabled ? access : EMPTY_MODULE_ACCESS, loaded: loaded || !enabled, reload: notifyModuleAccessChanged };
}
